import type { ReactElement } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import type { RoleName, User } from '../types/auth.types';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRoles?: RoleName[] | undefined;
  requireSuperAdmin?: boolean | undefined;
  requireCompanyAdmin?: boolean | undefined;
}

function hasRoleInList(roles: RoleName[], role: RoleName): boolean {
  return roles.includes(role);
}

function hasAnyRoleInList(roles: RoleName[], requiredRoles: RoleName[]): boolean {
  return requiredRoles.some((role) => hasRoleInList(roles, role));
}

function checkSuperAdminAccess(effectiveRoles: RoleName[], required: boolean): boolean {
  if (!required) return true;
  return hasRoleInList(effectiveRoles, 'SUPER_ADMIN');
}

function checkCompanyAdminAccess(effectiveRoles: RoleName[], required: boolean): boolean {
  if (!required) return true;
  return hasRoleInList(effectiveRoles, 'SUPER_ADMIN') || hasRoleInList(effectiveRoles, 'ADMIN');
}

function checkRequiredRolesAccess(effectiveRoles: RoleName[], roles?: RoleName[]): boolean {
  if (!roles || roles.length === 0) return true;
  return hasAnyRoleInList(effectiveRoles, roles);
}

interface RoleCheckContext {
  user: User;
  effectiveRoles: RoleName[];
}

function checkRoleAccess(
  context: RoleCheckContext,
  props: Pick<AuthGuardProps, 'requireSuperAdmin' | 'requireCompanyAdmin' | 'requiredRoles'>
): boolean {
  const { effectiveRoles } = context;
  return (
    checkSuperAdminAccess(effectiveRoles, props.requireSuperAdmin ?? false) &&
    checkCompanyAdminAccess(effectiveRoles, props.requireCompanyAdmin ?? false) &&
    checkRequiredRolesAccess(effectiveRoles, props.requiredRoles)
  );
}

export function AuthGuard({
  children,
  requiredRoles,
  requireSuperAdmin,
  requireCompanyAdmin,
}: AuthGuardProps): ReactElement {
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const getEffectiveRoles = useAuthStore((state) => state.getEffectiveRoles);

  if (!isInitialized) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const effectiveRoles = getEffectiveRoles();
  const hasAccess = checkRoleAccess(
    { user, effectiveRoles },
    { requireSuperAdmin, requireCompanyAdmin, requiredRoles }
  );

  if (!hasAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
